import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../../services/supabaseClient'
import { useAppSelector } from '../../store/hooks'
import { SECTIONS } from '../../data/sections'
import type { SectionId } from '../../types/tools'
import InlineAlert from '../base/InlineAlert'
import styles from './AssignmentManager.module.css'

interface Team { id: string; name: string }

interface AssignmentRow {
  id: number
  tool_id: string
  due_at: string
  message: string | null
  created_at: string | null
  team_id: string
}

interface ToolOption { id: string; label: string; categoryKey: string; categoryLabel: string }
interface CategoryOption { key: string; label: string; toolIds: string[] }

function buildToolOptions(): { tools: ToolOption[]; categories: CategoryOption[] } {
  const tools: ToolOption[] = []
  const categories: CategoryOption[] = []
  for (const sk of Object.keys(SECTIONS) as SectionId[]) {
    const cats = SECTIONS[sk].cats as unknown as Record<string, { label: string; tools: Record<string, { l: string }> }>
    for (const ck of Object.keys(cats)) {
      const cat = cats[ck]
      const toolIds: string[] = []
      for (const [tk, t] of Object.entries(cat.tools)) {
        tools.push({ id: tk, label: t.l, categoryKey: ck, categoryLabel: cat.label })
        toolIds.push(tk)
      }
      categories.push({ key: ck, label: cat.label, toolIds })
    }
  }
  return { tools, categories }
}

function defaultDueDateIso(): string {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  // yyyy-mm-dd for <input type="date">
  return d.toISOString().slice(0, 10)
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export default function AssignmentManager() {
  const profile = useAppSelector((s) => s.auth.profile)
  const isAdmin = profile?.app_role === 'admin'
  const isLeader = profile?.app_role === 'leader'
  const canManage = isAdmin || isLeader

  const { tools, categories } = useMemo(buildToolOptions, [])
  const toolLabel = useCallback(
    (id: string) => tools.find((t) => t.id === id)?.label ?? id,
    [tools]
  )

  const [teams, setTeams] = useState<Team[]>([])
  const [teamId, setTeamId] = useState<string>('')
  const [assignments, setAssignments] = useState<AssignmentRow[]>([])
  const [loadingAssignments, setLoadingAssignments] = useState(false)

  const [mode, setMode] = useState<'tool' | 'category'>('tool')
  const [selectedToolId, setSelectedToolId] = useState<string>(tools[0]?.id ?? '')
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>(categories[0]?.key ?? '')
  const [dueAt, setDueAt] = useState<string>(defaultDueDateIso())
  const [message, setMessage] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [alert, setAlert] = useState<{ variant: 'success' | 'error'; message: string } | null>(null)

  // Load teams the user is allowed to manage.
  useEffect(() => {
    async function loadTeams() {
      if (isAdmin) {
        const { data } = await supabase
          .from('teams')
          .select('id, name')
          .eq('status', 'active')
          .order('name')
        const rows = (data as Team[]) ?? []
        setTeams(rows)
        // Preselect the admin's own team if they have one; else first team.
        const initial = profile?.team_id && rows.some((t) => t.id === profile.team_id)
          ? profile.team_id
          : rows[0]?.id ?? ''
        setTeamId(initial)
      } else if (isLeader && profile?.team_id) {
        // Leader: only their own team. Still load its name for display.
        const { data } = await supabase
          .from('teams')
          .select('id, name')
          .eq('id', profile.team_id)
          .single()
        const rows = data ? [data as Team] : []
        setTeams(rows)
        setTeamId(profile.team_id)
      }
    }
    if (canManage) loadTeams()
  }, [isAdmin, isLeader, canManage, profile?.team_id])

  const loadAssignments = useCallback(async () => {
    if (!teamId) { setAssignments([]); return }
    setLoadingAssignments(true)
    const { data } = await supabase
      .from('assignments')
      .select('id, tool_id, due_at, message, created_at, team_id')
      .eq('team_id', teamId)
      .eq('status', 'active')
      .order('due_at', { ascending: true })
    setAssignments((data as AssignmentRow[]) ?? [])
    setLoadingAssignments(false)
  }, [teamId])

  useEffect(() => { loadAssignments() }, [loadAssignments])

  if (!canManage) {
    return (
      <div className={styles.card} data-testid="assignment-manager-denied">
        <p>You need to be a team leader or admin to manage assignments.</p>
      </div>
    )
  }

  const handleSubmit = async () => {
    setAlert(null)
    if (!teamId) { setAlert({ variant: 'error', message: 'Pick a team.' }); return }
    if (!dueAt) { setAlert({ variant: 'error', message: 'Pick a due date.' }); return }

    const dueAtIso = new Date(`${dueAt}T23:59:59`).toISOString()
    setSubmitting(true)

    if (mode === 'tool') {
      if (!selectedToolId) { setSubmitting(false); setAlert({ variant: 'error', message: 'Pick a tool.' }); return }
      const { error } = await supabase.rpc('create_assignment', {
        p_team_id: teamId,
        p_tool_id: selectedToolId,
        p_due_at: dueAtIso,
        p_message: message.trim() || undefined,
      })
      setSubmitting(false)
      if (error) { setAlert({ variant: 'error', message: error.message }); return }
      setAlert({ variant: 'success', message: `Assigned "${toolLabel(selectedToolId)}".` })
    } else {
      const cat = categories.find((c) => c.key === selectedCategoryKey)
      if (!cat || cat.toolIds.length === 0) {
        setSubmitting(false)
        setAlert({ variant: 'error', message: 'Pick a category with tools.' })
        return
      }
      const { error } = await supabase.rpc('create_topic_assignment', {
        p_team_id: teamId,
        p_tool_ids: cat.toolIds,
        p_due_at: dueAtIso,
        p_message: message.trim() || undefined,
      })
      setSubmitting(false)
      if (error) { setAlert({ variant: 'error', message: error.message }); return }
      setAlert({ variant: 'success', message: `Assigned all ${cat.toolIds.length} tools in "${cat.label}".` })
    }

    setMessage('')
    setDueAt(defaultDueDateIso())
    await loadAssignments()
  }

  const handleArchive = async (id: number) => {
    const { error } = await supabase.rpc('archive_assignment', { p_assignment_id: id })
    if (!error) await loadAssignments()
  }

  return (
    <div className={styles.card} data-testid="assignment-manager">
      <div className={styles.hdr}>Assignments</div>

      <div className={styles.row}>
        <label className={styles.label}>Team</label>
        <select
          className={styles.select}
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          disabled={!isAdmin || teams.length <= 1}
          data-testid="assignment-team-select"
        >
          {teams.length === 0 && <option value="">— No teams —</option>}
          {teams.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      <div className={styles.row}>
        <label className={styles.label}>Assign</label>
        <div className={styles.modeGroup} role="radiogroup">
          <label className={styles.radio}>
            <input
              type="radio"
              name="assign-mode"
              checked={mode === 'tool'}
              onChange={() => setMode('tool')}
              data-testid="assignment-mode-tool"
            />
            Single tool
          </label>
          <label className={styles.radio}>
            <input
              type="radio"
              name="assign-mode"
              checked={mode === 'category'}
              onChange={() => setMode('category')}
              data-testid="assignment-mode-category"
            />
            Whole category
          </label>
        </div>
      </div>

      {mode === 'tool' ? (
        <div className={styles.row}>
          <label className={styles.label}>Tool</label>
          <select
            className={styles.select}
            value={selectedToolId}
            onChange={(e) => setSelectedToolId(e.target.value)}
            data-testid="assignment-tool-select"
          >
            {categories.map((c) => (
              <optgroup key={c.key} label={c.label}>
                {tools.filter((t) => t.categoryKey === c.key).map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      ) : (
        <div className={styles.row}>
          <label className={styles.label}>Category</label>
          <select
            className={styles.select}
            value={selectedCategoryKey}
            onChange={(e) => setSelectedCategoryKey(e.target.value)}
            data-testid="assignment-category-select"
          >
            {categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label} ({c.toolIds.length} tools)
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={styles.row}>
        <label className={styles.label}>Due</label>
        <input
          type="date"
          className={styles.input}
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
          data-testid="assignment-due-input"
        />
      </div>

      <div className={styles.row}>
        <label className={styles.label}>Message</label>
        <input
          type="text"
          className={styles.inputWide}
          placeholder="Optional note shown with the assignment"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="assignment-message-input"
        />
      </div>

      <div className={styles.submitRow}>
        <button
          className={styles.btnPrimary}
          onClick={handleSubmit}
          disabled={submitting || !teamId}
          data-testid="assignment-submit-btn"
        >
          {submitting ? 'Assigning...' : 'Create assignment'}
        </button>
      </div>

      {alert && (
        <div className={styles.alertWrap} data-testid="assignment-alert">
          <InlineAlert
            message={alert.message}
            variant={alert.variant}
            onDismiss={() => setAlert(null)}
          />
        </div>
      )}

      <div className={styles.listHeader}>Active assignments</div>
      {loadingAssignments ? (
        <div className={styles.empty}>Loading...</div>
      ) : assignments.length === 0 ? (
        <div className={styles.empty} data-testid="assignments-empty">
          No active assignments for this team.
        </div>
      ) : (
        <table className={styles.table} data-testid="assignments-table">
          <thead>
            <tr>
              <th>Tool</th>
              <th>Due</th>
              <th>Message</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.id} data-testid={`assignment-row-${a.id}`}>
                <td>{toolLabel(a.tool_id)}</td>
                <td>{formatDate(a.due_at)}</td>
                <td className={styles.messageCell}>{a.message ?? ''}</td>
                <td className={styles.actionCell}>
                  <button
                    className={styles.btnSm}
                    onClick={() => handleArchive(a.id)}
                    data-testid={`assignment-archive-${a.id}`}
                  >
                    Archive
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
